import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone, MessageCircle } from 'lucide-react';
import HeroSectionComponent from '../components/HeroSection';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work5.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0 0.5rem;
    max-width: 100%;
  }

  @media (max-width: 389px) {
    padding: 0 0.25rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2rem, 5.5vw, 4.5rem);
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.1;
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(1.75rem, 5vw, 2.5rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(1.875rem, 5.25vw, 3rem);
    margin-bottom: 1.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    margin-bottom: 0.75rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.5rem);
  margin-bottom: 2.5rem;
  opacity: 0.95;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  /* Mobile adjustments */
  @media (max-width: 389px) {
    font-size: clamp(0.875rem, 2.25vw, 1.125rem);
    margin-bottom: 1.5rem;
    line-height: 1.5;
    max-width: 100%;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    font-size: clamp(0.9375rem, 2.375vw, 1.25rem);
    margin-bottom: 2rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    font-size: clamp(0.75rem, 2vw, 1rem);
    margin-bottom: 1rem;
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 389px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: white;

  svg {
    color: #FECE1B;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
  }

  @media (max-width: 389px) {
    padding: 0.4rem 0.75rem;
    margin-bottom: 1rem;
    font-size: 0.75rem;
    gap: 0.25rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.3rem 0.75rem;
    margin-bottom: 0.75rem;
    font-size: 0.7rem;
  }
`;

const StyledButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    padding: 0.875rem 1.75rem;
    font-size: 0.95rem;
    gap: 0.5rem;
  }

  @media (max-width: 427px) and (min-width: 390px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }

  @media (max-width: 389px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
    gap: 0.375rem;
  }

  /* Landscape mobile adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    gap: 0.25rem;
  }

  /* Icon sizing for mobile */
  @media (max-width: 768px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 389px) {
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (orientation: landscape) and (max-height: 500px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: white;
    box-shadow: 0 10px 30px rgba(254, 206, 27, 0.4);
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 15px 40px rgba(254, 206, 27, 0.5);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      transform: translateY(-2px);
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.4rem 0.6rem;
    gap: 0.25rem;
    
    span {
      font-size: 0.75rem;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media (max-width: 427px) and (min-width: 390px) {
    bottom: 1.25rem;
    padding: 0.35rem 0.5rem;
    
    span {
      font-size: 0.7rem;
    }
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: 389px) {
    bottom: 1rem;
    padding: 0.3rem 0.4rem;
    gap: 0.2rem;
    
    span {
      font-size: 0.65rem;
    }
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  position: relative;

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    gap: 0.875rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    flex-direction: row;
    gap: 0.75rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    flex-direction: column;
    align-items: center;
    gap: 0.875rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    flex-direction: column;
    align-items: center;
    gap: 0.625rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    flex-direction: row;
    gap: 0.5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 3rem;
  font-size: clamp(2rem, 4vw, 3rem);
`;

const AreasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const AreaCard = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  .icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    color: white;
  }

  h3 {
    color: #FECE1B;
    margin-bottom: 1rem;
    font-size: 1.5rem;
  }

  p {
    color: #666;
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  .services {
    list-style: none;
    padding: 0;
    margin-bottom: 1.5rem;

    li {
      color: #555;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }
  }

  .contact-info {
    background: #f8fafc;
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 1rem;

    p {
      margin: 0;
      font-size: 0.9rem;
      color: #666;
    }
  }
`;

const PostcodesSection = styled.section`
  background: #f8fafc;
  padding: 5rem 0;
`;

const PostcodesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const PostcodeCard = styled(motion.div)`
  background: white;
  padding: 1.5rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #FECE1B;
    transform: translateY(-2px);
  }

  .postcode {
    font-size: 1.2rem;
    font-weight: bold;
    color: #FECE1B;
    margin-bottom: 0.5rem;
  }

  .area {
    color: #666;
    font-size: 0.9rem;
  }
`;

const MapSection = styled.section`
  padding: 5rem 0;
  background: white;
`;

const MapContainer = styled.div`
  height: 500px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin: 3rem 0;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: white;
  padding: 5rem 0;
  text-align: center;
`;

const AreasPage: React.FC = () => {
  const areas = [
    {
      name: 'London',
      description: 'We serve all London boroughs with expert resin bound surfacing services. From central London to the outer boroughs, we bring quality and professionalism to every project.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Free surveys across all London boroughs'
    },
    {
      name: 'Surrey',
      description: 'Prestigious county to the south-west of London known for beautiful countryside, historic towns, and excellent quality of life. We deliver premium resin bound surfacing for Surrey\'s luxury properties.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Expert installation across Surrey'
    },
    {
      name: 'Kent',
      description: 'Garden of England to the south-east of London with historic cathedral cities and beautiful countryside. We provide premium resin bound surfacing for Kent\'s residential and commercial properties.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Expert installation across Kent'
    },
    {
      name: 'Essex',
      description: 'Historic county to the north-east of London across the Thames with thriving towns and beautiful countryside. We deliver premium resin bound surfacing for Essex\'s growing communities.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Expert installation across Essex'
    },
    {
      name: 'Hertfordshire',
      description: 'Beautiful county to the north of London with excellent transport links and charming market towns. We provide premium resin bound surfacing for Hertfordshire\'s residential and commercial properties.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Expert installation across Hertfordshire'
    },
    {
      name: 'Buckinghamshire',
      description: 'Historic county to the north-west of London known for beautiful countryside, historic towns, and excellent quality of life. We deliver premium resin bound surfacing for Buckinghamshire\'s luxury properties.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Expert installation across Buckinghamshire'
    },
    {
      name: 'Berkshire',
      description: 'Royal county to the west of London with historic towns, beautiful countryside, and excellent transport links. We provide premium resin bound surfacing for Berkshire\'s prestigious properties.',
      services: ['Resin Driveways', 'Garden Pathways', 'Pool Decks', 'Commercial Surfacing'],
      contact: 'Expert installation across Berkshire'
    }
  ];

  const postcodes = [
    { code: 'SW1', area: 'Westminster' },
    { code: 'SW2', area: 'Brixton' },
    { code: 'SW3', area: 'Chelsea' },
    { code: 'SW4', area: 'Clapham' },
    { code: 'SW5', area: 'Earl\'s Court' },
    { code: 'SW6', area: 'Fulham' },
    { code: 'SW7', area: 'South Kensington' },
    { code: 'SW8', area: 'South Lambeth' },
    { code: 'SW9', area: 'Stockwell' },
    { code: 'SW10', area: 'West Brompton' },
    { code: 'SW11', area: 'Battersea' },
    { code: 'SW12', area: 'Balham' },
    { code: 'SW13', area: 'Barnes' },
    { code: 'SW14', area: 'East Sheen' },
    { code: 'SW15', area: 'Putney' },
    { code: 'SW16', area: 'Streatham' },
    { code: 'SW17', area: 'Tooting' },
    { code: 'SW18', area: 'Wandsworth' },
    { code: 'SW19', area: 'Wimbledon' },
    { code: 'SW20', area: 'Raynes Park' },
    { code: 'KT1', area: 'Kingston upon Thames' },
    { code: 'KT2', area: 'Kingston upon Thames' },
    { code: 'KT3', area: 'New Malden' },
    { code: 'KT4', area: 'Worcester Park' },
    { code: 'KT5', area: 'Surbiton' },
    { code: 'KT6', area: 'Surbiton' },
    { code: 'KT7', area: 'Thames Ditton' },
    { code: 'KT8', area: 'West Molesey' },
    { code: 'KT9', area: 'Chessington' },
    { code: 'KT10', area: 'Esher' },
    { code: 'KT11', area: 'Cobham' },
    { code: 'KT12', area: 'Walton on Thames' },
    { code: 'KT13', area: 'Weybridge' },
    { code: 'KT14', area: 'West Byfleet' },
    { code: 'KT15', area: 'Addlestone' },
    { code: 'KT16', area: 'Chertsey' },
    { code: 'KT17', area: 'Epsom' },
    { code: 'KT18', area: 'Epsom' },
    { code: 'KT19', area: 'Epsom' },
    { code: 'KT20', area: 'Tadworth' },
    { code: 'KT21', area: 'Ashtead' },
    { code: 'KT22', area: 'Leatherhead' },
    { code: 'KT23', area: 'Leatherhead' },
    { code: 'KT24', area: 'Leatherhead' },
    { code: 'CR0', area: 'Croydon' },
    { code: 'CR2', area: 'South Croydon' },
    { code: 'CR3', area: 'Caterham' },
    { code: 'CR4', area: 'Mitcham' },
    { code: 'CR5', area: 'Coulsdon' },
    { code: 'CR6', area: 'Warlingham' },
    { code: 'CR7', area: 'Thornton Heath' },
    { code: 'CR8', area: 'Purley' },
    { code: 'CR9', area: 'Croydon' },
    { code: 'AL1', area: 'St Albans' },
    { code: 'AL2', area: 'St Albans' },
    { code: 'AL3', area: 'St Albans' },
    { code: 'AL4', area: 'St Albans' },
    { code: 'AL5', area: 'Harpenden' },
    { code: 'AL6', area: 'Welwyn' },
    { code: 'AL7', area: 'Welwyn Garden City' },
    { code: 'AL8', area: 'Welwyn Garden City' },
    { code: 'AL9', area: 'Welwyn Garden City' },
    { code: 'AL10', area: 'Hatfield' },
    { code: 'WD1', area: 'Watford' },
    { code: 'WD2', area: 'Watford' },
    { code: 'WD3', area: 'Rickmansworth' },
    { code: 'WD4', area: 'Kings Langley' },
    { code: 'WD5', area: 'Abbots Langley' },
    { code: 'WD6', area: 'Borehamwood' },
    { code: 'WD7', area: 'Radlett' },
    { code: 'WD17', area: 'Watford' },
    { code: 'WD18', area: 'Watford' },
    { code: 'WD19', area: 'Watford' },
    { code: 'WD23', area: 'Bushey' },
    { code: 'WD24', area: 'Bushey' },
    { code: 'WD25', area: 'Aldenham' },
    { code: 'HP1', area: 'Hemel Hempstead' },
    { code: 'HP2', area: 'Hemel Hempstead' },
    { code: 'HP3', area: 'Hemel Hempstead' },
    { code: 'HP4', area: 'Berkhamsted' },
    { code: 'HP5', area: 'Chesham' },
    { code: 'HP6', area: 'Amersham' },
    { code: 'HP7', area: 'Amersham' },
    { code: 'HP8', area: 'Chalfont St Giles' },
    { code: 'HP9', area: 'Beaconsfield' },
    { code: 'HP10', area: 'Beaconsfield' },
    { code: 'HP11', area: 'High Wycombe' },
    { code: 'HP12', area: 'High Wycombe' },
    { code: 'HP13', area: 'High Wycombe' },
    { code: 'HP14', area: 'High Wycombe' },
    { code: 'HP15', area: 'High Wycombe' },
    { code: 'HP16', area: 'Great Missenden' },
    { code: 'HP17', area: 'Aylesbury' },
    { code: 'HP18', area: 'Aylesbury' },
    { code: 'HP19', area: 'Aylesbury' },
    { code: 'HP20', area: 'Aylesbury' },
    { code: 'HP21', area: 'Aylesbury' },
    { code: 'HP22', area: 'Aylesbury' },
    { code: 'HP23', area: 'Tring' },
    { code: 'HP27', area: 'Princes Risborough' }
  ];

  return (
    <>
      <Helmet>
        <title>Areas We Cover | Resin Surfaces London | Surrey, Hertfordshire, Kent, Essex, Buckinghamshire & Berkshire</title>
        <meta name="description" content="Resin Surfaces London serves all London boroughs, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, and Berkshire. Find your postcode and get expert resin bound surfacing services in your area. Free surveys available." />
        <meta name="keywords" content="resin surfaces London areas, resin bound Surrey, resin surfacing Hertfordshire, London postcodes, Surrey postcodes, Hertfordshire postcodes" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/areas-we-cover/" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Resin Bound Surfacing Service Areas",
            "description": "Resin bound surfacing services across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Resin Surfaces London",
              "url": "https://www.resinsurfaceslondon.co.uk"
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "London"
              },
              {
                "@type": "City", 
                "name": "Surrey"
              },
              {
                "@type": "City",
                "name": "Hertfordshire"
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Areas We Cover"
        subtitle="Expert resin bound surfacing services across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire & Berkshire"
        badge="Professional Installation • Local Expertise • 10+ Year Warranty"
        backgroundImage="/images/resin-surfaces-bound-work5.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <SectionTitle>Our Service Areas</SectionTitle>
          <AreasGrid>
            {areas.map((area, index) => (
              <AreaCard
                key={area.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="icon">
                  <MapPin size={30} />
                </div>
                <h3>{area.name}</h3>
                <p>{area.description}</p>
                <ul className="services">
                  {area.services.map((service, i) => (
                    <li key={i}>• {service}</li>
                  ))}
                </ul>
                <div className="contact-info">
                  <p>{area.contact}</p>
                </div>
                <StyledButton to="/contact/" className="primary">
                  Get Quote <ArrowRight size={16} />
                </StyledButton>
              </AreaCard>
            ))}
          </AreasGrid>
        </Container>
      </Section>

      <MapSection>
        <Container>
          <SectionTitle>Our Coverage Map</SectionTitle>
          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d317715.7119263355!2d-0.3817834!3d51.5287352!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sLondon%2C%20UK!5e0!3m2!1sen!2suk!4v1634567890123!5m2!1sen!2suk"
              loading="lazy"
              title="Resin Surfaces London Coverage Area"
            ></iframe>
          </MapContainer>
        </Container>
      </MapSection>

      <PostcodesSection>
        <Container>
          <SectionTitle>Find Your Postcode</SectionTitle>
          <PostcodesGrid>
            {postcodes.map((postcode, index) => (
              <PostcodeCard
                key={postcode.code}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.01 }}
                viewport={{ once: true }}
              >
                <div className="postcode">{postcode.code}</div>
                <div className="area">{postcode.area}</div>
              </PostcodeCard>
            ))}
          </PostcodesGrid>
        </Container>
      </PostcodesSection>

      <CTASection>
        <Container>
          <SectionTitle style={{ color: 'white' }}>Ready to Transform Your Outdoor Space?</SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
            No matter where you are in our coverage area, we bring the same quality and professionalism to every project
          </p>
          <CTAButtons>
            <StyledButton to="/contact/" className="secondary">
              Get Free Quote <ArrowRight size={20} />
            </StyledButton>
            <StyledButton to="https://wa.me/447535935779" className="secondary">
              <MessageCircle size={20} /> WhatsApp Chat
            </StyledButton>
          </CTAButtons>
        </Container>
      </CTASection>
    </>
  );
};

export default AreasPage;
